import { Milliseconds } from 'src/types';
import { AddParametersType, CacheItemType } from './types';
import { KameleoonLogger } from 'src/logging';

interface ICacheManager<T> {
  /**
   * Add item to cache
   * @param {AddParametersType<T>} parameters - parameters for adding new item cache
   * @returns {void}
   */
  add({ key, data, lifetime }: AddParametersType<T>): void;
  /**
   * Get item that is not expired from cache
   * @param {string} key - key of item
   * @returns {T} an item that is not expired
   */
  getAliveItem(key: string): T | null;
}

/**
 * @class
 * CacheManager - a class for managing cache
 */
export class CacheManager<T> implements ICacheManager<T> {
  private cacheMap = new Map<string, CacheItemType<T>>();
  private intervalId: NodeJS.Timeout | null = null;
  private cleanupTimeout: number;

  /**
   * @param {number} cleanupTimeout - timeout for cleaning cache in seconds
   * generic type `T` is a type of data that will be stored in cache
   * @example
   * ```typescript
   * const cacheManager = new CacheManager<number>(60);
   * ```
   */
  constructor(cleanupTimeout: number) {
    this.cleanupTimeout = cleanupTimeout * Milliseconds.Second;
  }

  public add({ key, data, lifetime }: AddParametersType<T>): void {
    KameleoonLogger.debug`CALL: CacheManager.add(key: ${key}, data: ${data}, lifetime: ${lifetime})`;
    if (this.intervalId === null) {
      this.activate();
    }

    const expirationTime = lifetime * Milliseconds.Second;

    const cacheItem: CacheItemType<T> = {
      data,
      expirationTime: Date.now() + expirationTime,
    };

    this.cacheMap.set(key, cacheItem);
    KameleoonLogger.debug`RETURN: CacheManager.add(key: ${key}, data: ${data}, lifetime: ${lifetime})`;
  }

  public getAliveItem(key: string): T | null {
    KameleoonLogger.debug`CALL: CacheManager.getAliveItem(key: ${key})`;
    const cacheItem = this.cacheMap.get(key);

    if (cacheItem && cacheItem.expirationTime >= Date.now()) {
      const data = cacheItem.data;
      KameleoonLogger.debug`RETURN: CacheManager.getAliveItem(key: ${key}) -> (item: ${data})`;
      return data;
    }

    cacheItem && this.cacheMap.delete(key);

    KameleoonLogger.debug`RETURN: CacheManager.getAliveItem(key: ${key}) -> (item: null)`;
    return null;
  }

  private activate(): void {
    this.intervalId = setInterval(() => {
      const now = Date.now();

      for (const [key, item] of this.cacheMap) {
        if (item.expirationTime < now) {
          this.cacheMap.delete(key);
        }
      }

      if (!this.cacheMap.size) {
        this.cleanupInterval();
      }
    }, this.cleanupTimeout);
  }

  private cleanupInterval(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}
